import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { DEAL_STAGES, SUBSCRIPTION_PLANS, SERVICE_GROUPS, formatSGD } from "@/lib/dealStages";
import { usePipelineOptions } from "@/lib/pipelineOptions";
import { formatDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Mail, Phone, Trash2, Building2, User as UserIcon, Save, DollarSign, Calendar, Sparkles, Pencil } from "lucide-react";
import { ActivityTimeline } from "@/components/admin/ActivityTimeline";
import { TaskList } from "@/components/admin/TaskList";

const inputCls = "rounded-xl h-11";
const selectCls =
  "rounded-xl h-11 w-full px-3 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none";

export default function AdminCustomerDetail() {
  const { stages, plans, serviceGroups, getStageMeta, getPlanMeta } = usePipelineOptions();
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [contactForm, setContactForm] = useState(null);
  const [savingDeal, setSavingDeal] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const contactCardRef = useRef(null);
  const contactNameRef = useRef(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [dealRes, teamRes] = await Promise.all([
        api.get(`/admin/deals/${id}`),
        api.get("/admin/team").catch(() => ({ data: { items: [] } })),
      ]);
      const d = dealRes.data;
      setDeal(d);
      setForm({
        title: d.title || "",
        subscription_plan: d.subscription_plan || "essential",
        service_group: d.service_group || "",
        value_sgd: d.value_sgd ?? 0,
        stage: d.stage || "new",
        owner_id: d.owner_id || "",
        expected_close_date: d.expected_close_date || "",
        notes: d.notes || "",
      });
      if (d.contact) {
        setContactForm({
          name: d.contact.name || "",
          email: d.contact.email || "",
          phone: d.contact.phone || "",
          title: d.contact.title || "",
        });
      } else {
        // Always show an editable contact form even when no contact is linked yet
        setContactForm({ name: "", email: "", phone: "", title: "" });
      }
      setTeam(teamRes.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load customer");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);

  const saveDeal = async (e) => {
    e.preventDefault();
    if (loading || !form) return;
    setSavingDeal(true);
    try {
      const payload = {
        title: form.title.trim(),
        subscription_plan: form.subscription_plan,
        service_group: form.service_group || "",
        value_sgd: Number(form.value_sgd) || 0,
        stage: form.stage,
        owner_id: form.owner_id || null,
        expected_close_date: form.expected_close_date || null,
        notes: form.notes.trim() || null,
      };
      const { data } = await api.patch(`/admin/deals/${id}`, payload);
      setDeal((d) => ({ ...d, ...data }));
      toast.success("Customer updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSavingDeal(false);
    }
  };

  const saveContact = async (e) => {
    e.preventDefault();
    if (!contactForm) return;
    const name = contactForm.name.trim();
    const email = contactForm.email.trim();
    const phone = contactForm.phone.trim();
    const title = contactForm.title.trim();
    if (!name && !email && !phone && !title) {
      toast.error("Enter at least one contact detail before saving");
      return;
    }
    setSavingContact(true);
    try {
      const payload = {
        name: name || "(Unnamed contact)",
        email: email || null,
        phone: phone || null,
        title: title || null,
      };
      if (deal?.contact_id) {
        // Update existing contact
        const { data } = await api.patch(`/admin/contacts/${deal.contact_id}`, payload);
        setDeal((d) => ({ ...d, contact: data, contact_name: data.name, contact_email: data.email }));
        toast.success("Contact updated");
      } else {
        // Create new contact then link it to the deal
        const createPayload = { ...payload, account_id: deal?.account_id || undefined };
        // Strip nulls so ContactCreate doesn't reject the payload
        Object.keys(createPayload).forEach((k) => createPayload[k] == null && delete createPayload[k]);
        const { data: newContact } = await api.post("/admin/contacts", createPayload);
        await api.patch(`/admin/deals/${id}`, { contact_id: newContact.id });
        setDeal((d) => ({
          ...d,
          contact: newContact,
          contact_id: newContact.id,
          contact_name: newContact.name,
          contact_email: newContact.email,
        }));
        toast.success("Contact added");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save contact");
    } finally {
      setSavingContact(false);
    }
  };

  const startEditContact = () => {
    // Scroll to the contact card and focus the name input for immediate editing
    if (contactCardRef.current) {
      contactCardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setTimeout(() => {
      contactNameRef.current?.focus();
    }, 350);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/deals/${id}`);
      toast.success("Customer removed");
      navigate("/admin/pipeline");
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream">
        <AdminHeader />
        <main className="max-w-5xl mx-auto px-6 md:px-10 py-10">
          <p className="text-brand-slate/60 text-sm">Loading customer…</p>
        </main>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="min-h-screen bg-brand-cream">
        <AdminHeader />
        <main className="max-w-5xl mx-auto px-6 md:px-10 py-10">
          <p className="text-brand-slate/60 text-sm">Customer not found.</p>
          <button
            onClick={() => navigate("/admin/pipeline")}
            className="mt-4 inline-flex items-center gap-2 text-brand-orange"
            data-testid="customer-back-button"
          >
            <ArrowLeft size={14} /> Back to pipeline
          </button>
        </main>
      </div>
    );
  }

  const stage = getStageMeta(deal.stage);
  const plan = getPlanMeta(deal.subscription_plan);

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-customer-detail-page">
      <AdminHeader />

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <button
          onClick={() => navigate("/admin/pipeline")}
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-brand-navy/60 hover:text-brand-orange mb-6"
          data-testid="customer-back-button"
        >
          <ArrowLeft size={14} /> Back to Pipeline
        </button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] md:text-xs uppercase tracking-[0.45em] text-brand-orange font-semibold">Customer</p>
            <h1 className="text-3xl md:text-4xl font-semibold text-brand-navy mt-1" data-testid="customer-title">
              {deal.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <Badge className={`${stage.color} rounded-full`} data-testid="customer-status-badge">{stage.label}</Badge>
              {plan && <Badge className="bg-brand-navy text-white rounded-full" data-testid="customer-plan-badge">{plan.label}</Badge>}
              {deal.service_group && <Badge className="bg-brand-orange/10 text-brand-orange border border-brand-orange/30 rounded-full" data-testid="customer-service-group-badge">{deal.service_group}</Badge>}
              <span className="text-brand-navy font-semibold ml-2">{formatSGD(deal.value_sgd)}</span>
              {deal.owner_name && <span className="text-brand-slate/70">· PIC: {deal.owner_name}</span>}
            </div>
          </div>
          <div className="flex flex-col items-stretch md:items-end gap-2">
            <button
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 px-4 py-2 text-xs font-semibold rounded-full"
              data-testid="customer-delete-button"
            >
              <Trash2 size={14} /> Delete customer
            </button>
            <button
              onClick={startEditContact}
              className="inline-flex items-center justify-center gap-2 border border-brand-navy/20 text-brand-navy hover:bg-brand-cream px-4 py-2 text-xs font-semibold rounded-full"
              data-testid="customer-edit-contact-button"
            >
              <Pencil size={14} /> Edit contact
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Deal details */}
          <section className="lg:col-span-2 bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="customer-deal-card">
            <div className="flex items-center gap-2 mb-5">
              <DollarSign size={16} className="text-brand-orange" />
              <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-navy">Deal Details</h2>
            </div>

            <form onSubmit={saveDeal} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Customer Name" required>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                    required
                    className={inputCls}
                    data-testid="customer-input-title"
                  />
                </Field>
                <Field label="Subscription Plan" required>
                  <select
                    value={form.subscription_plan}
                    onChange={(e) => setForm((s) => ({ ...s, subscription_plan: e.target.value }))}
                    className={selectCls}
                    data-testid="customer-input-plan"
                  >
                    {plans.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </Field>
                <Field label="Contract Value (SGD)" required>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.value_sgd}
                    onChange={(e) => setForm((s) => ({ ...s, value_sgd: e.target.value }))}
                    className={inputCls}
                    data-testid="customer-input-value"
                  />
                </Field>
                <Field label="Status" required>
                  <select
                    value={form.stage}
                    onChange={(e) => setForm((s) => ({ ...s, stage: e.target.value }))}
                    className={selectCls}
                    data-testid="customer-input-stage"
                  >
                    {stages.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Service Group">
                  <select
                    value={form.service_group}
                    onChange={(e) => setForm((s) => ({ ...s, service_group: e.target.value }))}
                    className={selectCls}
                    data-testid="customer-input-service-group"
                  >
                    <option value="">— Optional —</option>
                    {serviceGroups.map((sg) => <option key={sg} value={sg}>{sg}</option>)}
                  </select>
                </Field>
                <Field label="Person In Charge (PIC)" required>
                  <select
                    value={form.owner_id}
                    onChange={(e) => setForm((s) => ({ ...s, owner_id: e.target.value }))}
                    className={selectCls}
                    data-testid="customer-input-pic"
                  >
                    <option value="">Unassigned</option>
                    {team.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
                  </select>
                </Field>
                <Field label="Expected Close Date">
                  <Input
                    type="date"
                    value={form.expected_close_date}
                    onChange={(e) => setForm((s) => ({ ...s, expected_close_date: e.target.value }))}
                    className={inputCls}
                    data-testid="customer-input-close"
                  />
                </Field>
              </div>
              <Field label="Notes">
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                  rows={4}
                  className="rounded-xl w-full px-3 py-2 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none"
                  data-testid="customer-input-notes"
                />
              </Field>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={savingDeal}
                  className="inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-2.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full transition-colors disabled:opacity-60"
                  data-testid="customer-save-deal"
                >
                  <Save size={14} /> {savingDeal ? "Saving…" : "Save deal details"}
                </button>
              </div>
            </form>
          </section>

          {/* Contact card */}
          <aside className="space-y-6">
            <section ref={contactCardRef} className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="customer-contact-card">
              <div className="flex items-center gap-2 mb-5">
                <UserIcon size={16} className="text-brand-orange" />
                <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-navy">Primary Contact</h2>
              </div>
              {contactForm && (
                <form onSubmit={saveContact} className="space-y-3">
                  <Field label="Name">
                    <Input
                      ref={contactNameRef}
                      value={contactForm.name}
                      onChange={(e) => setContactForm((s) => ({ ...s, name: e.target.value }))}
                      className={inputCls}
                      data-testid="customer-contact-name"
                    />
                  </Field>
                  <Field label="Title">
                    <Input
                      value={contactForm.title}
                      onChange={(e) => setContactForm((s) => ({ ...s, title: e.target.value }))}
                      className={inputCls}
                      data-testid="customer-contact-title"
                    />
                  </Field>
                  <Field label="Email">
                    <Input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm((s) => ({ ...s, email: e.target.value }))}
                      className={inputCls}
                      data-testid="customer-contact-email"
                    />
                  </Field>
                  <Field label="Phone">
                    <Input
                      value={contactForm.phone}
                      onChange={(e) => setContactForm((s) => ({ ...s, phone: e.target.value }))}
                      className={inputCls}
                      data-testid="customer-contact-phone"
                    />
                  </Field>
                  <div className="flex gap-2 pt-1">
                    {contactForm.email && (
                      <a href={`mailto:${contactForm.email}`} className="inline-flex items-center gap-1 bg-brand-navy text-white px-3 py-1.5 text-[11px] font-semibold rounded-full">
                        <Mail size={11} /> Email
                      </a>
                    )}
                    {contactForm.phone && (
                      <a href={`tel:${contactForm.phone}`} className="inline-flex items-center gap-1 border border-brand-navy/20 text-brand-navy px-3 py-1.5 text-[11px] font-semibold rounded-full">
                        <Phone size={11} /> Call
                      </a>
                    )}
                  </div>
                  <div className="flex justify-end pt-2">
                    <button
                      type="submit"
                      disabled={savingContact}
                      className="inline-flex items-center gap-2 bg-brand-orange text-white px-4 py-2 text-xs font-semibold hover:bg-brand-orange-hover rounded-full transition-colors disabled:opacity-60"
                      data-testid="customer-save-contact"
                    >
                      <Save size={12} /> {savingContact ? "Saving…" : (deal.contact_id ? "Save contact" : "Add contact")}
                    </button>
                  </div>
                </form>
              )}
            </section>

            {/* Meta */}
            <section className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="customer-meta-card">
              <div className="flex items-center gap-2 mb-4">
                <Building2 size={16} className="text-brand-orange" />
                <h2 className="text-sm font-semibold uppercase tracking-[0.25em] text-brand-navy">Account</h2>
              </div>
              <p className="text-sm text-brand-navy font-medium">{deal.account?.name || deal.account_name || "—"}</p>
              {deal.account?.industry && <p className="text-xs text-brand-slate/70 mt-1">Industry: {deal.account.industry}</p>}
              {deal.account?.company_size && <p className="text-xs text-brand-slate/70">Size: {deal.account.company_size}</p>}

              <div className="mt-5 pt-4 border-t border-brand-navy/10 space-y-2 text-xs text-brand-slate/70">
                <div className="flex items-center gap-2">
                  <Calendar size={11} /> Created {formatDate(deal.created_at)}
                </div>
                {deal.source && (
                  <div className="flex items-center gap-2 capitalize">
                    <Sparkles size={11} /> Source: {deal.source.replace("_", " ")}
                  </div>
                )}
                {deal.enquiry_id && (
                  <div className="text-brand-slate/60 pt-2 border-t border-brand-navy/5 mt-2">
                    Originated from a landing page enquiry.
                  </div>
                )}
              </div>
            </section>
          </aside>
        </div>

        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <TaskList dealId={deal.id} team={team} title="Follow-ups for this customer" />
          <ActivityTimeline dealId={deal.id} contactId={deal.contact_id} />
        </div>
      </main>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this customer?"
        description={`"${deal.title}" will be permanently removed. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
      />
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-sm text-brand-navy">
        {label}{required && <span className="text-brand-orange ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
